import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FileText, Shield } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

// Update this when the auditor-approved version goes live.
const EFFECTIVE_DATE = '[Insert Date]';

const SUPPORT_EMAIL = 'support@medscore.in';
const PRIVACY_EMAIL = 'privacy@medscore.in';

const Mail = ({ to }) => (
  <a href={`mailto:${to}`} className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">
    {to}
  </a>
);

// Content mirrors the auditor-approved "Terms & Conditions and Privacy Policy" document.
// Keep wording and numbering unchanged unless legal/audit signs off on revisions.
const termsSections = [
  {
    id: 'eligibility',
    title: '1. Eligibility',
    body: (
      <>
        <p>To access or use our Services, you must be:</p>
        <ul>
          <li>At least 18 years of age</li>
          <li>Legally capable of entering into a binding agreement</li>
        </ul>
        <p>By registering or using the Services, you represent that you meet these requirements.</p>
      </>
    )
  },
  {
    id: 'use-of-services',
    title: '2. Use of Services',
    body: (
      <>
        <p>You agree to use our Services only for lawful, authorized, and business-related purposes. You may not:</p>
        <ul>
          <li>Reproduce, redistribute, or commercially exploit our content or tools without permission</li>
          <li>Interfere with or disrupt the functionality of the platform</li>
          <li>Use the Services to access or analyze personal data unlawfully or without consent</li>
        </ul>
      </>
    )
  },
  {
    id: 'user-data',
    title: '3. User Data and Privacy',
    body: (
      <ul>
        <li>
          By using our Services, you agree to the data practices described in the{' '}
          <a href="#privacy-policy" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2">Privacy Policy</a>{' '}
          set out below.
        </li>
        <li>You are responsible for the accuracy, legality, and security of the data you provide or access through our platforms.</li>
        <li>We implement reasonable security measures but cannot guarantee the absolute protection of user data transmitted or stored on our systems.</li>
      </ul>
    )
  },
  {
    id: 'intellectual-property',
    title: '4. Intellectual Property',
    body: (
      <p>
        All technology, designs, algorithms, content, trademarks, and branding associated with MedScore and SafeCredits are the
        intellectual property of PCRA Technologies or its licensors. Unauthorized use or duplication is strictly prohibited.
      </p>
    )
  },
  {
    id: 'service-availability',
    title: '5. Service Availability',
    body: (
      <p>
        We make reasonable efforts to maintain uptime and availability, but we do not guarantee uninterrupted access to the
        Services. Maintenance, updates, or unforeseen events may cause temporary disruptions.
      </p>
    )
  },
  {
    id: 'warranties',
    title: '6. Warranties and Disclaimers',
    body: (
      <>
        <p>Our Services are provided “as is” and “as available.” We make no express or implied warranties regarding:</p>
        <ul>
          <li>The accuracy or reliability of results</li>
          <li>The suitability of the platform for your specific purpose</li>
          <li>Any third-party data or integrations</li>
        </ul>
      </>
    )
  },
  {
    id: 'limitation-of-liability',
    title: '7. Limitation of Liability',
    body: (
      <>
        <p>To the fullest extent permitted by applicable law, PCRA Technologies shall not be liable for:</p>
        <ul>
          <li>Any indirect, incidental, special, or consequential damages</li>
          <li>Loss of business, revenue, profits, or data</li>
          <li>Any decisions made based on the use of MedScore or SafeCredits</li>
        </ul>
      </>
    )
  },
  {
    id: 'termination',
    title: '8. Account Suspension or Termination',
    body: (
      <>
        <p>We reserve the right to suspend or terminate your access to our platforms at any time, without notice, if:</p>
        <ul>
          <li>You breach these Terms</li>
          <li>Your use poses legal, reputational, or operational risk to PCRA Technologies or others</li>
        </ul>
      </>
    )
  },
  {
    id: 'governing-law',
    title: '9. Governing Law and Jurisdiction',
    body: (
      <p>
        These Terms are governed by and interpreted in accordance with the laws of India. Any disputes shall be subject to the
        exclusive jurisdiction of the courts located in Hyderabad.
      </p>
    )
  },
  {
    id: 'changes',
    title: '10. Changes to Terms',
    body: (
      <p>
        We may update these Terms periodically. Continued use of the Services after changes are made constitutes acceptance of
        the revised Terms. We encourage users to review this page regularly.
      </p>
    )
  },
  {
    id: 'payment-terms',
    title: '11. Payment Terms',
    subsections: [
      {
        id: 'onboarding-fee',
        title: 'Onboarding Fee',
        body: (
          <p>
            A one-time onboarding fee is charged upon registration to activate your MedScore account. This fee is communicated to
            you before payment. Early adopters may be onboarded at ₹0 at PCRA Technologies' sole discretion.
          </p>
        )
      },
      {
        id: 'payment-methods',
        title: 'Payment Methods',
        body: (
          <p>
            Payments are accepted via Razorpay (UPI, debit/credit cards, net banking) and offline modes including bank transfer
            (NEFT/RTGS/IMPS), UPI, cash, or cheque payable to PCRA Technologies Pvt. Ltd. Offline payments must be confirmed by
            our team before account activation or renewal.
          </p>
        )
      },
      {
        id: 'taxes',
        title: 'Taxes',
        body: (
          <p>
            All fees are exclusive of applicable taxes. GST at 18% (or the prevailing statutory rate) is charged on all payments and
            will be reflected in your invoice.
          </p>
        )
      },
      {
        id: 'invoices',
        title: 'Invoices',
        body: (
          <p>
            A GST-compliant invoice is issued upon receipt of each payment, delivered to your registered email address. You are
            responsible for maintaining an accurate and reachable email address.
          </p>
        )
      },
      {
        id: 'failed-payments',
        title: 'Failed Payments',
        body: (
          <p>
            If a payment fails or is not received by the due date, access to services may be restricted or suspended until the
            outstanding amount is cleared.
          </p>
        )
      },
      {
        id: 'refunds',
        title: 'Refunds',
        body: (
          <p>
            Onboarding fees are non-refundable once your account has been activated. Billing fees paid for a cycle already in
            progress are non-refundable. Refund requests for exceptional circumstances must be submitted in writing to{' '}
            <Mail to={SUPPORT_EMAIL} /> within 7 days of the payment date and are subject to review.
          </p>
        )
      },
      {
        id: 'billing-cycle',
        title: 'Billing Cycle',
        body: (
          <p>
            MedScore operates on a recurring billing cycle. For distributors paying via Razorpay, the cycle is monthly (30 days).
            For distributors paying via offline methods (bank transfer, UPI, cash, cheque), the cycle is 28 days from the date of
            payment receipt.
          </p>
        )
      },
      {
        id: 'subscription-fee',
        title: 'Subscription Fee',
        body: (
          <p>
            The monthly subscription fee is calculated based on the number of active pharmacy customers linked to your account
            during the billing period, multiplied by the per-customer rate communicated at the time of onboarding. This rate may be
            revised with 30 days' prior notice.
          </p>
        )
      },
      {
        id: 'calls-recording',
        title: 'Calls & Recording Add-on',
        body: (
          <p>
            If you have opted for the call recording and IVR feature, usage charges for calls are billed separately and added to
            your subscription invoice for the same billing period.
          </p>
        )
      },
      {
        id: 'billing-date',
        title: 'Billing Date',
        body: (
          <p>
            Your next billing date is 28 days (offline) or 30 days (online) from your last payment date. You will receive reminders
            via SMS, WhatsApp, and/or email before the due date.
          </p>
        )
      },
      {
        id: 'late-payment',
        title: 'Late Payment',
        body: (
          <p>
            Failure to pay within 5 days after the billing due date may result in temporary suspension of your account. Access is
            restored upon receipt of the outstanding payment. Repeated late payments may lead to permanent termination of services.
          </p>
        )
      },
      {
        id: 'price-changes',
        title: 'Price Changes',
        body: (
          <p>
            PCRA Technologies reserves the right to revise subscription pricing. Any changes will be communicated at least 30 days
            in advance via email or in-app notification. Continued use of the service after the effective date constitutes
            acceptance of the new pricing.
          </p>
        )
      },
      {
        id: 'cancellation',
        title: 'Cancellation',
        body: (
          <p>
            You may cancel your subscription at any time by writing to <Mail to={SUPPORT_EMAIL} />. Cancellation takes effect at
            the end of the current billing cycle. No refunds are issued for unused days within a paid cycle.
          </p>
        )
      }
    ]
  }
];

const privacySections = [
  {
    id: 'information-we-collect',
    title: '12. Information We Collect',
    body: (
      <>
        <p>To operate our platforms and serve your account, we collect the following categories of information:</p>
        <ul>
          <li>
            <strong>Account &amp; identity details</strong> — your name, business name, registered address, mobile number, email
            address, drug license number, and GST number, collected at registration to set up and verify your account.
          </li>
          <li>
            <strong>Payment details</strong> — bank transfer references, UPI IDs, and transaction reference numbers for offline
            payments. Card and UPI payments made via Razorpay are processed and stored directly by Razorpay; we do not receive or
            store your card or UPI credentials.
          </li>
          <li>
            <strong>Business data</strong> — customer counts, outstanding balances, payment history, and other credit-related data
            you upload or that our platform generates as you use it.
          </li>
          <li>
            <strong>Usage data</strong> — pages visited, features used, login timestamps, and device/browser information, collected
            automatically to help us understand and improve platform performance.
          </li>
          <li>
            <strong>Communication records</strong> — logs of the SMS, WhatsApp, and email messages we send you, including billing
            reminders, account alerts, and service notifications.
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'how-we-use',
    title: '13. How We Use Your Information',
    body: (
      <ul>
        <li>
          <strong>To deliver the Service</strong> — activating your account, processing payments, generating invoices, and
          providing credit analytics and scoring.
        </li>
        <li>
          <strong>To manage billing</strong> — calculating your subscription fee and sending payment-status reminders via SMS,
          WhatsApp, and email.
        </li>
        <li>
          <strong>To message you on WhatsApp</strong> — we use the WhatsApp Business API (Meta) strictly for transactional
          messages such as billing reminders, payment confirmations, and account alerts. We will not send you promotional messages
          on WhatsApp without your consent.
        </li>
        <li>
          <strong>To improve the platform</strong> — analysing usage patterns to identify issues and guide new features.
        </li>
        <li>
          <strong>To meet legal obligations</strong> — including GST filing, IT Act compliance, and responding to valid court or
          regulatory orders.
        </li>
      </ul>
    )
  },
  {
    id: 'data-sharing',
    title: '14. Data Sharing',
    body: (
      <>
        <p>We share only what each service provider needs to do its job, and we never sell your personal data to anyone.</p>
        <ul>
          <li>
            <strong>Razorpay</strong> — processes online payments; Razorpay's own privacy policy governs the data it holds.
          </li>
          <li>
            <strong>Meta (WhatsApp Business API)</strong> — receives phone numbers and message content solely to deliver WhatsApp
            notifications, under Meta's data policy.
          </li>
          <li>
            <strong>SMS gateway providers</strong> (e.g. Twilio or a local provider) — receive phone numbers for the sole purpose of
            SMS delivery.
          </li>
          <li>
            <strong>Google Analytics</strong> — receives anonymised usage data to help us understand traffic; no personally
            identifiable information is shared.
          </li>
          <li>
            <strong>Legal and regulatory authorities</strong> — data may be disclosed where required by Indian law, courts, or
            regulators.
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'data-retention',
    title: '15. Data Retention',
    body: (
      <p>
        We keep your account and business data for as long as your account remains active, and for five years after it is closed,
        in line with Indian GST and accounting requirements. Payment records and invoices are retained for seven years. To request
        deletion of data that isn't subject to a legal retention requirement, write to <Mail to={PRIVACY_EMAIL} />.
      </p>
    )
  },
  {
    id: 'data-security',
    title: '16. Data Protection and Security',
    body: (
      <p>
        We protect your data using industry-standard safeguards, including encrypted storage, HTTPS transmission, and access
        controls. While we take every reasonable precaution, no system can guarantee absolute security. Should a data breach
        affect your information, we will notify you as required by applicable law.
      </p>
    )
  },
  {
    id: 'your-rights',
    title: '17. Your Rights',
    body: (
      <>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
          <li><strong>Correct</strong> — update incorrect or outdated information from your account, or by contacting us directly.</li>
          <li><strong>Delete</strong> — request deletion of your data, subject to our legal retention obligations.</li>
          <li>
            <strong>Opt out of communications</strong> — reply STOP to any SMS, or contact <Mail to={SUPPORT_EMAIL} /> to opt out
            of WhatsApp or email notifications. Critical account alerts — such as payment failure or account suspension notices —
            are exempt from opt-out, as they're necessary to keep your account secure.
          </li>
          <li><strong>Port your data</strong> — request your business data in a readable, portable format.</li>
        </ul>
      </>
    )
  },
  {
    id: 'cookies',
    title: '18. Cookies and Device Data',
    body: (
      <p>
        Our web platform uses cookies and local storage to keep you signed in and remember your preferences. We do not use
        third-party advertising cookies. You may disable cookies in your browser settings, though doing so may affect how well the
        platform works for you.
      </p>
    )
  }
];

const Section = ({ section }) => (
  <section id={section.id} className="legal-anchor mb-10">
    <h3 className="text-xl md:text-2xl font-bold text-white mb-4">{section.title}</h3>
    {section.body && <div className="legal-body">{section.body}</div>}
    {section.subsections?.map((sub) => (
      <div key={sub.id} id={sub.id} className="legal-anchor mt-6">
        <h4 className="text-base font-semibold text-cyan-300 mb-2">{sub.title}</h4>
        <div className="legal-body">{sub.body}</div>
      </div>
    ))}
  </section>
);

const TocGroup = ({ label, sections }) => (
  <div className="mb-6">
    <div className="text-xs mono text-slate-500 uppercase tracking-wider mb-3">{label}</div>
    <ul className="space-y-2">
      {sections.map((s) => (
        <li key={s.id}>
          <a href={`#${s.id}`} className="text-sm text-slate-400 hover:text-white transition-colors">
            {s.title}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

export const Legal = () => {
  const { pathname, hash } = useLocation();

  // /privacy-policy opens at the Privacy Policy part; any #hash jumps to that section.
  useEffect(() => {
    const target = hash ? hash.slice(1) : pathname === '/privacy-policy' ? 'privacy-policy' : null;
    const el = target && document.getElementById(target);
    if (el) {
      el.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  useEffect(() => {
    document.title = pathname === '/privacy-policy'
      ? 'Privacy Policy | PCRA Technologies'
      : 'Terms & Conditions | PCRA Technologies';
  }, [pathname]);

  return (
    <div className="w-full min-h-screen bg-slate-950 text-white relative">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;500;600;700;800&display=swap');

        * {
          font-family: 'Syne', sans-serif;
        }

        .mono {
          font-family: 'Space Mono', monospace;
        }

        .glass {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
        }

        .legal-anchor {
          scroll-margin-top: 7rem;
        }

        .legal-body {
          color: rgb(203 213 225);
          line-height: 1.75;
        }

        .legal-body p + p,
        .legal-body p + ul,
        .legal-body ul + p {
          margin-top: 0.75rem;
        }

        .legal-body ul {
          list-style: disc;
          padding-left: 1.5rem;
        }

        .legal-body li + li {
          margin-top: 0.5rem;
        }

        .legal-body strong {
          color: white;
          font-weight: 600;
        }
      `}</style>

      <Header />

      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20">
        {/* Page heading */}
        <div className="mb-12 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-black leading-tight mb-4">
            Terms &amp; Conditions and Privacy Policy
          </h1>
          <p className="text-sm text-slate-500 mono">Effective Date: {EFFECTIVE_DATE}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Table of contents */}
          <aside className="lg:w-72 flex-shrink-0">
            <nav className="glass rounded-2xl p-6 border border-white/10 lg:sticky lg:top-28 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
              <TocGroup label="Terms & Conditions" sections={termsSections} />
              <TocGroup label="Privacy Policy" sections={privacySections} />
            </nav>
          </aside>

          {/* Document */}
          <main className="flex-1 min-w-0 max-w-3xl">
            <div id="terms-and-conditions" className="legal-anchor">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Terms &amp; Conditions</h2>
              </div>
              <p className="legal-body mb-10">
                Please read these Terms &amp; Conditions (“Terms”) carefully before using any products or services offered by PCRA
                Technologies Pvt. Ltd. (“we,” “our,” or “us”). By accessing or using our platforms—MedScore and SafeCredits—you agree
                to be bound by these Terms.
              </p>
              {termsSections.map((section) => (
                <Section key={section.id} section={section} />
              ))}
            </div>

            <div id="privacy-policy" className="legal-anchor border-t border-white/10 pt-12 mt-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Privacy Policy</h2>
              </div>
              <p className="text-slate-400 italic mb-10">
                How PCRA Technologies collects, uses, and protects your information on MedScore and SafeCredits.
              </p>
              {privacySections.map((section) => (
                <Section key={section.id} section={section} />
              ))}
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Legal;
